import { useClerk, useOrganizationList, useUser } from "@clerk/nextjs";
import { IconAlertTriangle, IconChevronRight } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useReducer, useRef, useState } from "react";
import { z } from "zod";

import { useAuthorization } from "~/contexts/authorizationProvider";
import {
  canStartOrganizationSwitch,
  getOrganizationSwitchFailure,
  getOrganizationSwitchFailureMessage,
  getOrganizationSwitchRetryUrl,
  getSortedSwitchOrgMemberships,
  hasAuthoritativePaginatedMemberships,
  initialOrganizationSwitchState,
  organizationSwitchStateReducer,
  shouldShowMembershipLoadError,
  shouldShowPartialMembershipLoadError,
  shouldShowPartialMembershipsLoading,
  withOrganizationSwitchTimeout,
} from "~/modules/org/switch-org-state";
import { loggerService } from "~/services/logger.service";
import {
  Button,
  Divider,
  Flex,
  Image,
  Loader,
  LoadingOverlay,
  mantineColorRamps,
  Stack,
  showNotification,
  Text,
  TextInput,
  Tooltip,
  useForm,
  zodResolver,
} from "~/ui";
import { api } from "~/utils/api";
import { useOrgRouter } from "~/utils/paths";

import { DangerAlert } from "./Alert";
import styles from "./OrganizationSwitcher.module.css";

export interface SelectedOrganization {
  id: string;
  name: string;
  slug: string | null;
}

interface OrganizationSwitcherProps {
  allowSelectCurrent?: boolean;
  onSwitch?: (org: SelectedOrganization) => void;
}

const orgSlugPathParam = "[orgSlug]";
const orgSlugPrefix = "org";

const getNormalizedOrgSlug = (orgSlug: string) => `${orgSlugPrefix}-${orgSlug}`;
const normalizePath = (routerPathname: string, orgSlug: string) => {
  const normalizedOrgSlug = getNormalizedOrgSlug(orgSlug);
  const segments = routerPathname.split("/");
  const dynamicIndex = segments.findIndex(segment => {
    const isDynamicSegment = segment.startsWith("[") && segment.endsWith("]");
    if (!isDynamicSegment) {
      return false;
    }
    const isOrgSlugPath = segment === orgSlugPathParam;
    return !isOrgSlugPath;
  });
  const basePath = dynamicIndex === -1 ? routerPathname : segments.slice(0, dynamicIndex).join("/");
  const orgPath = basePath.replace(orgSlugPathParam, normalizedOrgSlug);
  return orgPath;
};

const getOrganizationRedirectUrl = (routerPathname: string, orgSlug: string) => {
  if (routerPathname.includes(orgSlugPathParam)) {
    return normalizePath(routerPathname, orgSlug);
  }
  return "/";
};

const _logoSize = 30;

/** Org membership rows are stable within a session; re-fetch at most once every five minutes. */
const ORG_MEMBERSHIPS_STALE_TIME_MS = 300_000;

interface OrganizationSwitcherButtonProps {
  id: string;
  disabled: boolean;
  clerkId: string;
  enabled: boolean;
  iconUrl: string | null;
  name: string;
  onClick: (orgId: string) => void;
}
const OrganizationSwitcherButton = (props: OrganizationSwitcherButtonProps) => {
  const orgIconUrl = props.iconUrl ?? "https://placehold.co/30x30.png";
  return (
    <Button
      disabled={props.disabled}
      variant="subtle"
      onClick={() => props.onClick(props.clerkId)}
      className={styles.switchButton}
      h={_logoSize + 10}
      w="100%"
    >
      <Flex align="center" gap="md" w="100%">
        <Flex>
          <Image alt={`Logo for ${props.name}`} fit="contain" height={_logoSize} width={_logoSize} src={orgIconUrl} radius="sm" />
        </Flex>
        <Flex style={{ flex: 1 }}>
          <Text>{props.name}</Text>
        </Flex>
        {!props.enabled && (
          <Flex>
            <Tooltip label="This Org has been disabled" withArrow withinPortal>
              <IconAlertTriangle color={mantineColorRamps.yellow[6]} size={20} />
            </Tooltip>
          </Flex>
        )}
        <Flex>
          <IconChevronRight />
        </Flex>
      </Flex>
    </Button>
  );
};

const createOrgFormSchema = z.object({
  name: z.string().trim().min(1, "Org Name is required"),
});
type CreateOrgFormSchemaType = z.infer<typeof createOrgFormSchema>;
interface CreateOrgFormProps {
  onSuccess: () => void;
}
const CreateOrgForm = (props: CreateOrgFormProps) => {
  const utils = api.useContext();
  const createOrgMutation = api.clerk.createOrg.useMutation({
    onSuccess: async org => {
      // The org list is cached past a modal close, so a new org would not appear without this.
      await utils.rbac.findUserOrgs.invalidate();
      showNotification({
        color: "green",
        title: "Organization created",
        message: `Organization "${org.name}" created successfully. It may take a few moments for Clerk to fully sync.`,
      });
      props.onSuccess();
    },
  });
  const form = useForm<CreateOrgFormSchemaType>({
    initialValues: { name: "" },
    validate: zodResolver(createOrgFormSchema),
  });

  const handleSubmit = (values: CreateOrgFormSchemaType) => {
    createOrgMutation.mutate({
      name: values.name,
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <LoadingOverlay visible={createOrgMutation.isPending} overlayBlur={2} />
      <Flex direction="row" gap="xs" style={{ flex: 1 }} w="100%">
        <TextInput {...form.getInputProps("name")} placeholder="New Org name" w="100%" withAsterisk />
        <Button type="submit">Create</Button>
      </Flex>
    </form>
  );
};

export const OrganizationSwitcher = ({ allowSelectCurrent = false, ...props }: OrganizationSwitcherProps): JSX.Element => {
  const { organization, getOrganization, setActive } = useClerk();
  const { isLoaded: userLoaded, user } = useUser();
  const organizationList = useOrganizationList({ userMemberships: { pageSize: 1000 } });
  const router = useOrgRouter();
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuthorization();

  const [organizationSwitchState, dispatchOrganizationSwitch] = useReducer(organizationSwitchStateReducer, initialOrganizationSwitchState);
  const currentAttemptRef = useRef(0);
  const setActiveStartedRef = useRef(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  // The modal mounts this component on every open, and org membership changes are rare: cache the
  // rows for the session so reopening the switcher does not put the list back into a loading state.
  const userOrgMembershipsQuery = api.rbac.findUserOrgs.useQuery(undefined, { staleTime: ORG_MEMBERSHIPS_STALE_TIME_MS });
  const userOrgMemberships = useMemo(() => {
    if (!userOrgMembershipsQuery.data) {
      return [];
    }
    return userOrgMembershipsQuery.data.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  }, [userOrgMembershipsQuery.data]);

  const paginatedMemberships = organizationList.userMemberships.data;
  const paginatedMembershipsLoaded = hasAuthoritativePaginatedMemberships({
    organizationListLoaded: organizationList.isLoaded,
    membershipsError: organizationList.userMemberships.isError,
    membershipsLoading: organizationList.userMemberships.isLoading,
  });
  const clerkMemberships = useMemo(
    () =>
      getSortedSwitchOrgMemberships({
        userMemberships: user?.organizationMemberships ?? [],
        paginatedMemberships: paginatedMemberships ?? [],
      }),
    [paginatedMemberships, user?.organizationMemberships],
  );
  const showMembershipLoadError = shouldShowMembershipLoadError({
    fallbackMemberships: clerkMemberships,
    membershipsError: organizationList.userMemberships.isError,
    userLoaded,
  });
  const showPartialMembershipsLoading = shouldShowPartialMembershipsLoading({
    fallbackMemberships: clerkMemberships,
    membershipsError: organizationList.userMemberships.isError,
    paginatedMembershipsLoaded,
    userLoaded,
  });
  const showPartialMembershipLoadError = shouldShowPartialMembershipLoadError({
    fallbackMemberships: clerkMemberships,
    membershipsError: organizationList.userMemberships.isError,
    userLoaded,
  });
  const clerkOrganizationsReady = userLoaded && !showMembershipLoadError && (clerkMemberships.length > 0 || paginatedMembershipsLoaded);

  // The loaded memberships answer for every org the user belongs to. Anything they do not cover —
  // a super admin's RBAC rows reach orgs outside their own memberships — is fetched by id, so no
  // row is ever dropped from the list on account of what Clerk happened to return.
  const resolveClerkOrganization = useCallback(
    async (orgClerkId: string): Promise<SelectedOrganization | null> => {
      const membership = clerkMemberships.find(item => item.organization.id === orgClerkId);
      if (membership?.organization.slug) {
        return {
          id: membership.organization.id,
          name: membership.organization.name,
          slug: membership.organization.slug,
        };
      }
      try {
        const clerkOrg = await withOrganizationSwitchTimeout(() => getOrganization(orgClerkId));
        if (!clerkOrg.slug) {
          return null;
        }
        return { id: clerkOrg.id, name: clerkOrg.name, slug: clerkOrg.slug };
      } catch {
        return null;
      }
    },
    [clerkMemberships, getOrganization],
  );

  const clearCache = async () => {
    await queryClient.cancelQueries();
    queryClient.removeQueries();
  };

  const redirectToOrg = (orgSlug: string) => {
    window.location.assign(getOrganizationRedirectUrl(router.pathname, orgSlug));
  };

  const retryOrganizationSwitchInFreshDocument = async (orgClerkId: string) => {
    const attempt = currentAttemptRef.current + 1;
    currentAttemptRef.current = attempt;
    dispatchOrganizationSwitch({ type: "start", source: "manual", organizationId: orgClerkId });

    const clerkOrg = await resolveClerkOrganization(orgClerkId);
    if (attempt !== currentAttemptRef.current) {
      return;
    }
    if (!clerkOrg?.slug) {
      loggerService.warn("Organization could not be verified", { orgClerkId });
      showNotification({
        color: "yellow",
        title: "Unable to select Organization",
        message: "Organization could not be verified",
      });
      dispatchOrganizationSwitch({ type: "failed", organizationId: orgClerkId, failure: "error" });
      return;
    }
    window.location.assign(
      getOrganizationSwitchRetryUrl({
        organizationId: clerkOrg.id,
        redirectUrl: getOrganizationRedirectUrl(router.pathname, clerkOrg.slug),
      }),
    );
  };

  const _handleSelectOrganization = async (orgClerkId: string) => {
    if (setActiveStartedRef.current) {
      if (organizationSwitchState.status === "failed") {
        void retryOrganizationSwitchInFreshDocument(orgClerkId);
      }
      return;
    }
    if (!canStartOrganizationSwitch(organizationSwitchState, "manual")) {
      return;
    }
    const attempt = currentAttemptRef.current + 1;
    currentAttemptRef.current = attempt;
    dispatchOrganizationSwitch({ type: "start", source: "manual", organizationId: orgClerkId });

    try {
      const clerkOrg = await resolveClerkOrganization(orgClerkId);
      if (attempt !== currentAttemptRef.current) {
        return;
      }
      if (!clerkOrg?.slug) {
        loggerService.warn("Organization could not be verified", { orgClerkId });
        showNotification({
          color: "yellow",
          title: "Unable to select Organization",
          message: "Organization could not be verified",
        });
        dispatchOrganizationSwitch({ type: "failed", organizationId: orgClerkId, failure: "error" });
        return;
      }

      // prevent redirects if the slug has not changed
      const currentOrgSlug = router.query.orgSlug;
      const newOrgSlug = getNormalizedOrgSlug(clerkOrg.slug);
      if (currentOrgSlug === newOrgSlug) {
        dispatchOrganizationSwitch({ type: "reset", organizationId: orgClerkId });
        return;
      }

      setActiveStartedRef.current = true;
      await withOrganizationSwitchTimeout(() => setActive({ organization: clerkOrg.id }));
      if (attempt !== currentAttemptRef.current) {
        return;
      }
      await clearCache();
      if (attempt !== currentAttemptRef.current) {
        return;
      }
      redirectToOrg(clerkOrg.slug);

      if (props.onSwitch) {
        props.onSwitch(clerkOrg);
      }
    } catch (error) {
      if (attempt !== currentAttemptRef.current) {
        return;
      }
      const failure = getOrganizationSwitchFailure(error);
      loggerService.warn("Organization switch failed", { failure, organizationId: orgClerkId });
      currentAttemptRef.current += 1;
      dispatchOrganizationSwitch({ type: "failed", organizationId: orgClerkId, failure });
    }
  };

  // React Query v5: `isPending` is the first-load-in-flight flag and turns false once the query
  // settles, including when it settles in error (where `data` stays undefined forever).
  const rbacLoading = userOrgMembershipsQuery.isPending;
  const clerkOrganizationsLoading = userLoaded && !showMembershipLoadError && !clerkOrganizationsReady;
  const showOrganizationsLoading = rbacLoading || !userLoaded || clerkOrganizationsLoading;
  const isLoading = organizationSwitchState.status === "switching";
  const isError = userOrgMembershipsQuery.isError;

  return (
    <Stack spacing="xs">
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      {isError && (
        <DangerAlert title="Unable to retrieve User Orgs">
          <Text>{userOrgMembershipsQuery.error.message}</Text>
        </DangerAlert>
      )}
      {organizationSwitchState.status === "failed" && (
        <DangerAlert title="Unable to switch organization">
          <Text>{getOrganizationSwitchFailureMessage(organizationSwitchState.failure)}</Text>
        </DangerAlert>
      )}
      {showOrganizationsLoading && !isError && (
        <Flex justify="center" align="center" gap="xs" mih={36}>
          <Loader size="sm" />
          <Text size="sm" color="dimmed">
            Loading organizations
          </Text>
        </Flex>
      )}
      {clerkOrganizationsReady &&
        userOrgMemberships.map(x => (
          <OrganizationSwitcherButton
            key={`orgSwitcherButton_${x.id}`}
            id={x.id}
            clerkId={x.clerkId}
            disabled={!allowSelectCurrent && x.clerkId === organization?.id}
            enabled={x.isEnabled}
            iconUrl={x.iconUrl}
            name={x.name}
            onClick={_handleSelectOrganization}
          />
        ))}
      {/* No RBAC row at all: the user holds no org roles, so there is nothing to switch into. Without
          this the modal body would be empty with no explanation. */}
      {clerkOrganizationsReady &&
        !showOrganizationsLoading &&
        !isError &&
        !showPartialMembershipsLoading &&
        !showPartialMembershipLoadError &&
        userOrgMemberships.length === 0 && (
          <Text size="sm" color="dimmed" align="center">
            No organizations are available to switch into.
          </Text>
        )}
      {showPartialMembershipsLoading && (
        <Flex justify="center" align="center" gap="xs">
          <Loader size="xs" />
          <Text size="sm" color="dimmed">
            Loading more organizations
          </Text>
        </Flex>
      )}
      {(showMembershipLoadError || showPartialMembershipLoadError) && (
        <Stack spacing={4} align="center">
          <Text size="sm" color={showPartialMembershipLoadError ? "dimmed" : "red"} align="center">
            {showPartialMembershipLoadError
              ? "Some organizations could not be loaded."
              : "Unable to load organizations. Check your connection and try again."}
          </Text>
          <Button
            size="xs"
            variant="subtle"
            loading={organizationList.userMemberships.isFetching}
            onClick={() => {
              const { revalidate } = organizationList.userMemberships;
              if (revalidate) {
                void revalidate().catch(() => undefined);
              }
            }}
          >
            Retry
          </Button>
        </Stack>
      )}
      {isSuperAdmin && (
        <>
          <Divider my="sm" />
          {!isCreatingNew && (
            <Button
              onClick={() => {
                setIsCreatingNew(true);
              }}
            >
              Create New Org
            </Button>
          )}
          {isCreatingNew && (
            <CreateOrgForm
              onSuccess={() => {
                setIsCreatingNew(false);
              }}
            />
          )}
        </>
      )}
    </Stack>
  );
};
